/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import BottomPartForm from './BottomPartForm';
import { openPaydAdvancedModel } from './Models';
import { ReactComponent as IbanIcon } from 'assets/globe.svg';
import { ReactComponent as NonIbanIcon } from 'assets/temple.svg';
import { CURRENCY_TYPES, RECIPIENT_TYPES, PAYMENT_METHOD, PAYMENT_PROVIDERS } from 'components/common/constants';
import i18nContext from 'components/i18n-context';
import { amountFormattedValue, isBeneficiaryStateProvider, prepareFieldErrors, strToFloat } from 'services/utils';
import { getConveraRequirements } from '../converaRequirements';
import { IconButton } from 'uikit/IconButton/IconButton';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';

const IBAN_TAB = 'IBAN';
const NON_IBAN_TAB = 'NON_IBAN';

const OpenPaydAdvancedForm = ({
  customerNumber,
  calculateCommission,
  checkTransfer,
  iban,
  setIban,
  setIsNonIban,
  checkIban,
  isIbanCheckLoading,
  ibanCredentials,
  ibanWarning,
  isRepeatPayment,
  clearIbanCredentials,
  uploadDocuments,
  validationSchema,
  paymentMethod,
  account,
  uploadedFiles,
  commission,
  removePaymentFile,
  error,
  ibanValidationError,
  setError,
  isCommissionLoading,
  isLoading,
  isSuccess,
  commonForm,
  countries,
  providerType,
  isFormResetAfterAccountChanging,
  setIsPreparedTransactionsCreation,
  isForApprovalTransactionEdit,
  isUserCanCreatePreparedTransaction,
  isUserCanSendTransaction,
  validateForApprovalTransaction,
  paymentPurposes
}) => {
  const i18n = useContext(i18nContext);
  const model = openPaydAdvancedModel;
  const form = useFormik(validationSchema);
  // Merge general error and IBAN validation error
  const effectiveError = error || ibanValidationError;
  const serverFieldsErrors = prepareFieldErrors(i18n, effectiveError);
  const [disabled, setDisabled] = useState(true);
  const [activeTab, setActiveTab] = useState(
    (isRepeatPayment && !iban) ||
      (!validationSchema.initialValues?.iban && !account.multi_wallet_number && paymentMethod !== PAYMENT_METHOD.SWIFT)
      ? NON_IBAN_TAB
      : IBAN_TAB
  );
  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    validateField,
    validateForm,
    resetForm,
    setFieldValue,
    setFieldError,
    setErrors
  } = commonForm || form;

  useEffect(() => {
    if (activeTab === NON_IBAN_TAB) {
      setIsNonIban(true);
    } else {
      setIsNonIban(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (isRepeatPayment && !iban) {
      setActiveTab(NON_IBAN_TAB);
    }
  }, [isRepeatPayment, iban]);

  useEffect(() => {
    const asyncOperations = async () => {
      if (!isFormResetAfterAccountChanging) return;

      if (
        (isRepeatPayment && !values.recipient_wallet && activeTab === NON_IBAN_TAB) ||
        (!isRepeatPayment && activeTab === IBAN_TAB)
      ) {
        if (values.amount && paymentMethod) {
          await calculateCommission(customerNumber, {
            walletNumber: account?.wallet_number,
            paymentMethod: paymentMethod,
            amount: strToFloat(values?.amount?.toString() || ''),
            bankCountry: ibanCredentials?.bank_country || values.bank_country
          });
        }

        if (!isForApprovalTransactionEdit) {
          await handleCheckTransfer(undefined);
        }
      }
    };

    asyncOperations();
  }, [paymentMethod, isRepeatPayment, isFormResetAfterAccountChanging, activeTab]);

  useEffect(() => {
    if (!iban && !ibanCredentials) {
      resetForm();
    } else {
      const asyncOperations = async () => {
        await handleIbanComplete(false);
      };

      asyncOperations();
    }
  }, [account.wallet_number]);

  useEffect(() => {
    if (iban && !values.recipient_wallet) {
      setFieldValue('recipient_wallet', iban);
    }
  }, [iban, values.recipient_wallet]);

  useEffect(() => {
    if (isSuccess) {
      resetForm({});
      setDisabled(true);
    }
  }, [isSuccess]);

  // Clear payment_purpose when bank_country changes from UAE to another country
  useEffect(() => {
    if (values.bank_country && values.bank_country !== 'AE' && values.payment_purpose) {
      setFieldValue('payment_purpose', '');
    }
  }, [values.bank_country]);

  const handleRecipientTypeChange = (name, data) => handleChange({ target: { name, value: data } });

  const bankCountry = ibanCredentials?.bank_country || values.bank_country;
  // TRN-7602: Convera API / Merchant — the Routing Code and / or the State are mandatory for some bank countries.
  const { isRoutingCodeRequired, isStateRequired } = getConveraRequirements({
    provider: providerType,
    paymentMethod,
    bankCountry,
    hasIban: !!values.recipient_wallet
  });

  // The rules follow the bank country at once: when it changes, an already shown form is validated again (the mandatory
  // marks come with the render, the errors — here), so the submit can't go through with a now-missing field.
  // TRN-7648: the form stays `disabled` while a now-mandatory field is empty, so the shown errors of the
  // country-dependent fields are re-checked on their own — otherwise they stay after the field becomes optional.
  useEffect(() => {
    if (!disabled) {
      validateForm();
      return;
    }
    ['sort_code', 'beneficiary_state'].forEach((field) => errors[field] && validateField(field));
  }, [bankCountry]);

  // Routing code makes sense when the recipient's bank is identified manually (no IBAN) — or when the bank country
  // requires it (TRN-7602: e.g. Azerbaijan, Brazil have IBANs but Convera wants the Routing Code).
  // TRN-7638: openPaydAdvancedModel applies the same rule to send nothing while hidden — keep them in sync.
  const isRoutingCodeShown =
    (activeTab === NON_IBAN_TAB || isRoutingCodeRequired) && paymentMethod === PAYMENT_METHOD.SWIFT;

  // TRN-7625: a field that stops being shown must not keep its value (e.g. a Brazilian IBAN replaced with a German one)
  // — it would be sent with no way to see or clear it. Not while the IBAN is being checked (the bank country is unknown
  // until then), and only after the field was shown, so a value prefilled on opening the form survives the first check.
  const wasRoutingCodeShownRef = useRef(false);
  useEffect(() => {
    if (isRoutingCodeShown) {
      wasRoutingCodeShownRef.current = true;
    } else if (!isIbanCheckLoading && wasRoutingCodeShownRef.current) {
      wasRoutingCodeShownRef.current = false;
      setFieldValue('sort_code', '');
      setFieldError('sort_code', undefined);
    }
  }, [isRoutingCodeShown, isIbanCheckLoading]);

  const currentPaymentPurposes =
    paymentPurposes?.find((item) => item.transferProvider === providerType)?.paymentPurposes?.[bankCountry] || null;
  const shouldShowPaymentPurpose = !!currentPaymentPurposes;
  const paymentPurposeOptions = currentPaymentPurposes
    ? Object.entries(currentPaymentPurposes).map(([key, value]) => ({ key, value: `${key} - ${value}` }))
    : [];
  const isPaymentPurposeMissing = shouldShowPaymentPurpose && !values.payment_purpose;

  const handleCheckTransfer = async (fieldName) => {
    if (fieldName) await validateField(fieldName);
    if (
      !serverFieldsErrors?.iban &&
      (activeTab === IBAN_TAB
        ? values.recipient_wallet &&
          (ibanCredentials?.bank_country || values.bank_country) &&
          (ibanCredentials?.bank_name || values.bank_name) &&
          (ibanCredentials?.account_number || values.account_number) &&
          (ibanCredentials?.bic || values.bic)
        : values.bank_country && values.bank_name && values.account_number && values.bic) &&
      values.recipient_type &&
      (values.recipient_type === RECIPIENT_TYPES.COMPANY
        ? values.company_name
        : values.first_name && values.last_name) &&
      values.registration_number &&
      values.beneficiary_city &&
      values.beneficiary_country &&
      values.beneficiary_post_code &&
      values.beneficiary_address &&
      (isRoutingCodeRequired ? values.sort_code : true) &&
      (isStateRequired ? values.beneficiary_state : true) &&
      values.amount &&
      values.comment &&
      (shouldShowPaymentPurpose ? values.payment_purpose : true)
    ) {
      setDisabled(false);
      const errorsResult = await validateForm();

      if (!Object.keys(errorsResult).length) {
        await checkTransfer(
          customerNumber,
          model({
            providerTableData: values,
            data: { wallet: account, paymentMethod, ...ibanCredentials }
          })
        );
      }
    }
  };

  const handleActiveTab = (activeTab) => {
    if (ibanCredentials) clearIbanCredentials();
    if (error) setError(null);
    if (activeTab === NON_IBAN_TAB) {
      setFieldValue('recipient_wallet', '');
      delete errors['recipient_wallet'];
    }
    if (activeTab === IBAN_TAB) {
      setFieldValue('bank_country', null);
      setFieldValue('bank_name', '');
      setFieldValue('account_number', '');
      setFieldValue('bic', '');
      setFieldValue('sort_code', '');
      delete errors['bank_country'];
      delete errors['bank_name'];
      delete errors['account_number'];
      delete errors['bic'];
      delete errors['sort_code'];
    }
    setErrors(errors);
    setActiveTab(activeTab);
  };

  const onAmountIBANChange = (e) => {
    handleChange(e);
    if (e.target.name === 'recipient_wallet') {
      setIban(e.target.value);
    }
  };

  const getCommissionValue = () => {
    if (!commission.value && !commission.currency) {
      return '0.00';
    }

    return commission.commissionDetails?.displayAmount
      ? `${commission.commissionDetails.displayAmount}`
      : `${amountFormattedValue(commission.value)} ${commission.currency}`;
  };

  const handleIbanComplete = async (causedOnBlur) => {
    if (causedOnBlur) await validateField('recipient_wallet');
    values.recipient_wallet && (await checkIban(values.recipient_wallet));
    if (causedOnBlur && !isForApprovalTransactionEdit) await handleCheckTransfer(undefined);
  };

  // Editing a "For approval" payment skips the per-field server check (checkTransfer) — still run the client-side
  // (Yup) validation on blur so an emptied/invalid required field is highlighted with its error.
  const handleFieldBlur = (fieldName) =>
    isForApprovalTransactionEdit ? validateField(fieldName) : handleCheckTransfer(fieldName);

  const bottomPartProps = {
    validateField,
    validateForm,
    values,
    errors,
    error,
    handleChange,
    handleSubmit,
    serverFieldsErrors,
    onAmountIBANChange,
    calculateCommission,
    handleCheckTransfer,
    getCommissionValue,
    currency: account.currency,
    currencyPrecision: account?.currency_precision,
    paymentMethod,
    paymentProvider: account.transfer_provider,
    ibanCredentials,
    isSubmitDisabled:
      (isForApprovalTransactionEdit ? false : disabled) ||
      isLoading ||
      isCommissionLoading ||
      !!Object.keys(errors).length ||
      !!Object.keys(serverFieldsErrors).length ||
      !!error ||
      !paymentMethod ||
      isPaymentPurposeMissing,
    isLoading: isLoading || isCommissionLoading,
    uploadedFiles,
    uploadDocuments,
    removePaymentFile,
    customerNumber,
    currentWalletNumber: account?.wallet_number,
    commission,
    setIsPreparedTransactionsCreation,
    isForApprovalTransactionEdit,
    isUserCanCreatePreparedTransaction,
    isUserCanSendTransaction,
    validateForApprovalTransaction,
    model: openPaydAdvancedModel,
    setError,
    shouldShowPaymentPurpose,
    paymentPurposeOptions
  };

  return (
    <>
      {(account.currency !== CURRENCY_TYPES.EUR ||
        ((providerType === PAYMENT_PROVIDERS.MANUAL ||
          providerType === PAYMENT_PROVIDERS.H_AND_H ||
          providerType === PAYMENT_PROVIDERS.DUKASCOPY ||
          providerType === PAYMENT_PROVIDERS.CONVERA ||
          providerType === PAYMENT_PROVIDERS.CONVERA_MERCHANT ||
          providerType === PAYMENT_PROVIDERS.CONVERA_API) &&
          PAYMENT_METHOD.SWIFT)) && (
        <div className={'navigation-payment'}>
          <IconButton
            className={'navigation-payment-button'}
            text={i18n.getMessage('transfer.label.iban')}
            active={activeTab === IBAN_TAB}
            onClick={() => handleActiveTab(IBAN_TAB)}
            Icon={IbanIcon}
          />
          <IconButton
            className={'navigation-payment-button'}
            text={i18n.getMessage('transfer.divider.advanceForm')}
            active={activeTab === NON_IBAN_TAB}
            onClick={() => !isIbanCheckLoading && handleActiveTab(NON_IBAN_TAB)}
            Icon={NonIbanIcon}
          />
        </div>
      )}
      {activeTab === IBAN_TAB && (
        <Input
          isRequired={true}
          autoComplete={'off'}
          label={i18n.getMessage('transfer.form.recipientIban.label')}
          name={'recipient_wallet'}
          value={values.recipient_wallet}
          error={errors.recipient_wallet || serverFieldsErrors.iban || serverFieldsErrors.recipientIban}
          warning={ibanWarning}
          onChange={onAmountIBANChange}
          onBlur={() => handleIbanComplete(true)}
          subText={
            ibanCredentials?.bank_name &&
            i18n.getMessage('transfer.form.bankName.subText', { bankName: ibanCredentials?.bank_name })
          }
        />
      )}
      {(activeTab === NON_IBAN_TAB || activeTab === IBAN_TAB) && paymentMethod === PAYMENT_METHOD.SWIFT && (
        <>
          <div className={'inputs-wrapper-evenly'}>
            <InputDropDownSearch
              isRequired={true}
              isDisabled={isIbanCheckLoading || !!ibanCredentials?.bank_country}
              autoComplete={'off'}
              label={i18n.getMessage('transfer.form.bankCountry.label')}
              name={'bank_country'}
              value={ibanCredentials?.bank_country || values.bank_country}
              onChange={handleRecipientTypeChange}
              options={countries.map((value) => ({ key: value, value: i18n.getMessage(`country.${value}`) }))}
              error={errors.bank_country || serverFieldsErrors.bank_country}
              onBlur={() => handleFieldBlur('bank_country')}
            />
            <Input
              isRequired={true}
              isDisabled={isIbanCheckLoading || !!ibanCredentials?.bank_name}
              autoComplete={'off'}
              label={i18n.getMessage('transfer.form.bankName.label')}
              name={'bank_name'}
              value={ibanCredentials?.bank_name || values.bank_name}
              onChange={handleChange}
              error={errors.bank_name || serverFieldsErrors.bank_name}
              onBlur={() => handleFieldBlur('bank_name')}
            />
          </div>
          <div className={'inputs-wrapper-evenly'}>
            <Input
              isRequired={true}
              isDisabled={isIbanCheckLoading || !!ibanCredentials?.account_number}
              autoComplete={'off'}
              label={i18n.getMessage('transfer.form.accountNumber.label')}
              name={'account_number'}
              value={ibanCredentials?.account_number || values.account_number}
              onChange={handleChange}
              error={errors.account_number || serverFieldsErrors.accountNumber}
              onBlur={() => handleFieldBlur('account_number')}
            />
            <Input
              isRequired={true}
              isDisabled={isIbanCheckLoading || !!ibanCredentials?.bic}
              autoComplete={'off'}
              label={i18n.getMessage('transfer.form.BIC.label')}
              error={errors?.bic || serverFieldsErrors?.bic}
              name={'bic'}
              value={ibanCredentials?.bic || values.bic}
              onChange={handleChange}
              onBlur={() => handleFieldBlur('bic')}
            />
          </div>
        </>
      )}
      {isRoutingCodeShown && (
        <Input
          isRequired={isRoutingCodeRequired}
          autoComplete={'off'}
          label={i18n.getMessage('transfer.form.routingCode.label')}
          name={'sort_code'}
          value={values.sort_code}
          error={errors.sort_code || serverFieldsErrors.sort_code}
          onChange={handleChange}
          onBlur={() => handleFieldBlur('sort_code')}
        />
      )}
      <div className={`inputs-wrapper ${values.recipient_type}`}>
        <InputDropDown
          label={i18n.getMessage('transfer.form.recipientType.label')}
          autoComplete={'off'}
          name={'recipient_type'}
          value={values.recipient_type}
          onChange={handleRecipientTypeChange}
          options={Object.values(RECIPIENT_TYPES).map((value) => ({ key: value, value }))}
          isDisabled={isIbanCheckLoading}
        />
        {values.recipient_type === RECIPIENT_TYPES.COMPANY && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.companyName.label')}
            name={'company_name'}
            value={values.company_name}
            error={errors.company_name || serverFieldsErrors.companyName}
            onChange={handleChange}
            onBlur={() => handleFieldBlur('company_name')}
            isDisabled={isIbanCheckLoading}
          />
        )}
        {values.recipient_type === RECIPIENT_TYPES.INDIVIDUAL && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.firstName.label')}
            name={'first_name'}
            value={values.first_name}
            error={errors.first_name || serverFieldsErrors.firstName}
            onChange={handleChange}
            onBlur={() => handleFieldBlur('first_name')}
            isDisabled={isIbanCheckLoading}
          />
        )}
        {values.recipient_type === RECIPIENT_TYPES.INDIVIDUAL && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.lastName.label')}
            name={'last_name'}
            value={values.last_name}
            error={errors.last_name || serverFieldsErrors.lastName}
            onChange={handleChange}
            onBlur={() => handleFieldBlur('last_name')}
            isDisabled={isIbanCheckLoading}
          />
        )}
      </div>
      <div className={'inputs-wrapper-evenly'}>
        <Input
          isRequired={true}
          autoComplete={'off'}
          label={
            values.recipient_type === RECIPIENT_TYPES.COMPANY
              ? i18n.getMessage('transfer.form.registrationNumber.label')
              : i18n.getMessage('transfer.form.identificationNumber.label')
          }
          name={'registration_number'}
          value={values.registration_number}
          onChange={handleChange}
          error={errors.registration_number || serverFieldsErrors?.registrationNumber}
          onBlur={() => handleFieldBlur('registration_number')}
          isDisabled={isIbanCheckLoading}
        />
        <Input
          isRequired={true}
          autoComplete={'off'}
          label={i18n.getMessage('transfer.form.beneficiaryCity.label')}
          name={'beneficiary_city'}
          value={values.beneficiary_city}
          onChange={handleChange}
          error={errors.beneficiary_city || serverFieldsErrors.beneficiary_city}
          onBlur={() => handleFieldBlur('beneficiary_city')}
          isDisabled={isIbanCheckLoading}
        />
      </div>
      <div className={'inputs-wrapper-evenly'}>
        <InputDropDownSearch
          isRequired={true}
          autoComplete={'off'}
          label={i18n.getMessage('transfer.form.beneficiaryCountry.label')}
          name={'beneficiary_country'}
          value={values.beneficiary_country}
          onChange={handleRecipientTypeChange}
          options={countries.map((value) => ({ key: value, value: i18n.getMessage(`country.${value}`) }))}
          error={errors.beneficiary_country || serverFieldsErrors.beneficiary_country}
          onBlur={() => handleFieldBlur('beneficiary_country')}
          isDisabled={isIbanCheckLoading}
        />
        <Input
          isRequired={true}
          autoComplete={'off'}
          label={i18n.getMessage('transfer.form.beneficiaryPostalCode.label')}
          name={'beneficiary_post_code'}
          value={values.beneficiary_post_code}
          onChange={handleChange}
          error={errors.beneficiary_post_code || serverFieldsErrors.beneficiary_post_code}
          onBlur={() => handleFieldBlur('beneficiary_post_code')}
          isDisabled={isIbanCheckLoading}
        />
      </div>
      {isBeneficiaryStateProvider(providerType) && (
        <Input
          isRequired={isStateRequired}
          autoComplete={'off'}
          label={i18n.getMessage('transfer.form.beneficiaryState.label')}
          name={'beneficiary_state'}
          value={values.beneficiary_state}
          onChange={handleChange}
          error={errors.beneficiary_state || serverFieldsErrors.beneficiary_state}
          onBlur={() => handleFieldBlur('beneficiary_state')}
          isDisabled={isIbanCheckLoading}
        />
      )}
      <Input
        isRequired={true}
        autoComplete={'off'}
        label={i18n.getMessage('transfer.form.beneficiaryAddress.label')}
        name={'beneficiary_address'}
        value={values.beneficiary_address}
        onChange={handleChange}
        error={errors.beneficiary_address || serverFieldsErrors.beneficiary_address}
        onBlur={() => handleFieldBlur('beneficiary_address')}
        isDisabled={isIbanCheckLoading}
      />
      <BottomPartForm {...bottomPartProps} />
    </>
  );
};

OpenPaydAdvancedForm.propTypes = {
  customerNumber: PropTypes.string,
  calculateCommission: PropTypes.func.isRequired,
  checkTransfer: PropTypes.func.isRequired,
  uploadDocuments: PropTypes.func.isRequired,
  iban: PropTypes.string,
  setIban: PropTypes.func,
  setIsNonIban: PropTypes.func,
  checkIban: PropTypes.func.isRequired,
  ibanCredentials: PropTypes.object,
  ibanWarning: PropTypes.string,
  clearIbanCredentials: PropTypes.func,
  paymentMethod: PropTypes.string,
  isRepeatPayment: PropTypes.bool,
  isIbanCheckLoading: PropTypes.bool,
  validationSchema: PropTypes.object,
  account: PropTypes.object,
  commission: PropTypes.object,
  isCommissionLoading: PropTypes.bool,
  isLoading: PropTypes.bool,
  error: PropTypes.any,
  setError: PropTypes.func,
  uploadedFiles: PropTypes.array,
  removePaymentFile: PropTypes.func,
  commonForm: PropTypes.object,
  currency: PropTypes.string,
  isSuccess: PropTypes.bool,
  countries: PropTypes.array,
  providerType: PropTypes.string,
  isFormResetAfterAccountChanging: PropTypes.bool,
  setIsPreparedTransactionsCreation: PropTypes.func,
  isForApprovalTransactionEdit: PropTypes.bool,
  isUserCanCreatePreparedTransaction: PropTypes.bool,
  isUserCanSendTransaction: PropTypes.bool,
  validateForApprovalTransaction: PropTypes.func,
  paymentPurposes: PropTypes.object
};

export default OpenPaydAdvancedForm;
